/**
 * Heatmap generation utilities using Kernel Density Estimation (KDE)
 *
 * Generates a density heatmap from UV coordinate points for 360° gaze visualization.
 * Handles the horizontal wrap-around at the edges of equirectangular projections.
 *
 * Performance optimizations:
 * - Flat Float32Array kernel for cache-friendly access
 * - Pre-computed color lookup table (LUT) for O(1) color mapping
 * - Float32Array density grid for faster typed array operations
 * - Inline maxDensity tracking during accumulation
 * - Adaptive resolution based on device capabilities
 */

import type {
  UVCoordinates,
  WeightedUVCoordinates,
  HeatmapGenerationConfig,
  HeatmapGenerationResult,
  HeatmapColorScheme,
} from "../types/gazeHeatmapTypes";

/** Default configuration for heatmap generation */
export const DEFAULT_HEATMAP_CONFIG: HeatmapGenerationConfig = {
  width: 2048,
  height: 1024,
  kernelRadius: 30,
  kernelSigma: 15,
  colorScheme: "thermal",
};

/** Adaptive resolution for low-end devices */
export const LOW_END_HEATMAP_CONFIG: HeatmapGenerationConfig = {
  width: 1024,
  height: 512,
  kernelRadius: 15,
  kernelSigma: 8,
  colorScheme: "thermal",
};

/**
 * Color gradient definitions for different schemes
 * Each gradient is an array of [position, r, g, b, a] tuples
 * Position is 0-1, colors are 0-255, alpha is 0-1
 */
const COLOR_GRADIENTS: Record<HeatmapColorScheme, [number, number, number, number, number][]> = {
  // Green scheme - matches the app's green accent color
  // Lowered visibility threshold from 20% to 5% for better trajectory/heatmap coherence
  green: [
    [0.0, 0, 0, 0, 0], // Transparent
    [0.05, 200, 255, 200, 0.15], // Very light green - visible from 5%
    [0.15, 200, 255, 200, 0.3], // Light green
    [0.35, 0, 200, 137, 0.5], // Primary green (#00C889)
    [0.6, 0, 175, 115, 0.7], // Medium green
    [0.8, 0, 150, 100, 0.85], // Darker green
    [1.0, 0, 100, 50, 0.95], // Deep green
  ],
  // Thermal scheme - classic heatmap colors
  // Lowered visibility threshold from 20% to 5% for better trajectory/heatmap coherence
  thermal: [
    [0.0, 0, 0, 0, 0], // Transparent
    [0.05, 0, 0, 255, 0.2], // Light blue - visible from 5%
    [0.15, 0, 0, 255, 0.4], // Blue
    [0.3, 0, 255, 255, 0.6], // Cyan
    [0.5, 0, 255, 0, 0.7], // Green
    [0.75, 255, 255, 0, 0.85], // Yellow
    [1.0, 255, 0, 0, 0.95], // Red
  ],
  // Viridis scheme - scientific visualization standard
  // Lowered visibility threshold from 20% to 5% for better trajectory/heatmap coherence
  viridis: [
    [0.0, 0, 0, 0, 0], // Transparent
    [0.05, 68, 1, 84, 0.2], // Very dark purple - visible from 5%
    [0.15, 68, 1, 84, 0.4], // Dark purple
    [0.3, 59, 82, 139, 0.55], // Blue-purple
    [0.5, 33, 145, 140, 0.7], // Teal
    [0.75, 94, 201, 98, 0.85], // Green
    [1.0, 253, 231, 37, 0.95], // Yellow
  ],
};

/**
 * Interpolate color from a gradient at a given position
 *
 * @param gradient - Color gradient definition
 * @param t - Position in gradient (0-1)
 * @returns RGBA values
 */
function interpolateColor(
  gradient: [number, number, number, number, number][],
  t: number,
): [number, number, number, number] {
  // Clamp t to [0, 1]
  t = Math.max(0, Math.min(1, t));

  // Find the two gradient stops to interpolate between
  let lowerStop = gradient[0];
  let upperStop = gradient[gradient.length - 1];

  for (let i = 0; i < gradient.length - 1; i++) {
    if (t >= gradient[i][0] && t <= gradient[i + 1][0]) {
      lowerStop = gradient[i];
      upperStop = gradient[i + 1];
      break;
    }
  }

  // Calculate interpolation factor
  const range = upperStop[0] - lowerStop[0];
  const factor = range > 0 ? (t - lowerStop[0]) / range : 0;

  // Interpolate each channel
  const r = Math.round(lowerStop[1] + factor * (upperStop[1] - lowerStop[1]));
  const g = Math.round(lowerStop[2] + factor * (upperStop[2] - lowerStop[2]));
  const b = Math.round(lowerStop[3] + factor * (upperStop[3] - lowerStop[3]));
  const a = Math.round((lowerStop[4] + factor * (upperStop[4] - lowerStop[4])) * 255);

  return [r, g, b, a];
}

/** LUT size for color mapping (256 entries = 1KB per gradient) */
const COLOR_LUT_SIZE = 256;

/** Cache for pre-computed color LUTs per scheme */
const colorLUTCache: Map<HeatmapColorScheme, Uint8Array> = new Map();

/**
 * Create a pre-computed color lookup table for O(1) color mapping
 *
 * @param colorScheme - The color scheme to create LUT for
 * @returns Uint8Array of size 256 * 4 (RGBA for each of 256 density levels)
 */
function createColorLUT(colorScheme: HeatmapColorScheme): Uint8Array {
  // Return cached LUT if available
  const cached = colorLUTCache.get(colorScheme);
  if (cached) return cached;

  const lut = new Uint8Array(COLOR_LUT_SIZE * 4);
  const gradient = COLOR_GRADIENTS[colorScheme];

  for (let i = 0; i < COLOR_LUT_SIZE; i++) {
    const t = i / (COLOR_LUT_SIZE - 1);
    const [r, g, b, a] = interpolateColor(gradient, t);
    const idx = i * 4;
    lut[idx] = r;
    lut[idx + 1] = g;
    lut[idx + 2] = b;
    lut[idx + 3] = a;
  }

  colorLUTCache.set(colorScheme, lut);
  return lut;
}

/**
 * Flat Gaussian kernel structure for cache-friendly access
 */
interface FlatGaussianKernel {
  data: Float32Array;
  size: number;
}

/**
 * Create a pre-computed Gaussian kernel as a flat Float32Array
 * This is more cache-friendly than a 2D array due to contiguous memory access
 *
 * @param radius - Kernel radius in pixels
 * @param sigma - Gaussian sigma
 * @returns Flat kernel with size metadata
 */
function createGaussianKernel(radius: number, sigma: number): FlatGaussianKernel {
  const size = radius * 2 + 1;
  const kernel = new Float32Array(size * size);
  const sigma2 = 2 * sigma * sigma;

  for (let y = 0; y < size; y++) {
    const yOffset = y * size;
    const dy = y - radius;
    const dy2 = dy * dy;

    for (let x = 0; x < size; x++) {
      const dx = x - radius;
      const distance2 = dx * dx + dy2;
      kernel[yOffset + x] = Math.exp(-distance2 / sigma2);
    }
  }

  return { data: kernel, size };
}

/**
 * Generate a density heatmap from UV coordinate points
 * Supports both regular and weighted points (for video heatmap fade-out effect)
 *
 * Performance optimizations applied:
 * - Float32Array density grid for faster typed array operations
 * - Flat Float32Array kernel for cache-friendly access
 * - Pre-computed color LUT for O(1) color mapping
 * - Inline maxDensity tracking to avoid separate scan
 *
 * @param points - Array of UV coordinates (optionally with weight)
 * @param config - Heatmap generation configuration
 * @returns Heatmap generation result with ImageData
 */
export function generateHeatmap(
  points: (UVCoordinates | WeightedUVCoordinates)[],
  config: Partial<HeatmapGenerationConfig> = {},
): HeatmapGenerationResult {
  const startTime = performance.now();

  // Merge with defaults
  const fullConfig: HeatmapGenerationConfig = {
    ...DEFAULT_HEATMAP_CONFIG,
    ...config,
  };

  const { width, height, kernelRadius, kernelSigma, colorScheme } = fullConfig;

  // Create density grid using Float32Array for faster typed array operations
  const density = new Float32Array(width * height);

  // Create flat Gaussian kernel for cache-friendly access
  const kernel = createGaussianKernel(kernelRadius, kernelSigma);
  const kernelSize = kernel.size;
  const kernelData = kernel.data;

  // Check once if points have weights to avoid repeated type checks
  const hasWeights = points.length > 0 && "weight" in points[0];

  // Track maxDensity during accumulation to avoid separate scan
  let maxDensity = fullConfig.maxDensity ?? 0;
  const trackMax = maxDensity === 0;

  // Apply kernel to each point
  for (let i = 0; i < points.length; i++) {
    const point = points[i];

    // Get weight (default to 1.0 for non-weighted points)
    const weight = hasWeights ? (point as WeightedUVCoordinates).weight : 1.0;

    // Skip zero-weight points
    if (weight <= 0) continue;

    // Convert UV to pixel coordinates
    // U maps directly to X (left to right)
    // V is inverted because: UV convention has V=0 at bottom, V=1 at top
    // but canvas has Y=0 at top, Y=height at bottom
    const centerX = Math.floor(point.u * width);
    const centerY = Math.floor((1 - point.v) * height);

    // Apply kernel with weight multiplier
    for (let ky = 0; ky < kernelSize; ky++) {
      const py = centerY + ky - kernelRadius;
      if (py < 0 || py >= height) continue;

      const kernelRowOffset = ky * kernelSize;
      const densityRowOffset = py * width;

      for (let kx = 0; kx < kernelSize; kx++) {
        // Handle horizontal wrap-around
        let px = centerX + kx - kernelRadius;
        if (px < 0) px += width;
        else if (px >= width) px -= width;

        const densityIdx = densityRowOffset + px;
        const newValue = density[densityIdx] + kernelData[kernelRowOffset + kx] * weight;
        density[densityIdx] = newValue;

        // Track max inline to avoid separate scan
        if (trackMax && newValue > maxDensity) {
          maxDensity = newValue;
        }
      }
    }
  }

  // Create ImageData
  const imageData = new ImageData(width, height);
  const data = imageData.data;

  // Use pre-computed color LUT for O(1) color mapping
  const colorLUT = createColorLUT(colorScheme);
  const invMaxDensity = maxDensity > 0 ? (COLOR_LUT_SIZE - 1) / maxDensity : 0;

  // Convert density to colors using LUT
  for (let i = 0; i < density.length; i++) {
    // Map density to LUT index (0-255)
    const lutIdx = Math.min(COLOR_LUT_SIZE - 1, Math.floor(density[i] * invMaxDensity)) * 4;

    const pixelIdx = i * 4;
    data[pixelIdx] = colorLUT[lutIdx];
    data[pixelIdx + 1] = colorLUT[lutIdx + 1];
    data[pixelIdx + 2] = colorLUT[lutIdx + 2];
    data[pixelIdx + 3] = colorLUT[lutIdx + 3];
  }

  const processingTime = performance.now() - startTime;

  return {
    imageData,
    maxDensity,
    pointCount: points.length,
    processingTime,
  };
}

/**
 * Sample points to reduce processing time for large datasets
 *
 * @param points - Array of points
 * @param maxPoints - Maximum number of points to keep
 * @returns Sampled array of points
 */
export function samplePoints<T>(points: T[], maxPoints: number): T[] {
  if (points.length <= maxPoints) return points;

  const sampledPoints: T[] = [];
  const step = points.length / maxPoints;

  for (let i = 0; i < maxPoints; i++) {
    const idx = Math.floor(i * step);
    sampledPoints.push(points[idx]);
  }

  return sampledPoints;
}

/**
 * Generate a legend image for the heatmap
 *
 * @param colorScheme - Color scheme to use
 * @param width - Legend width in pixels
 * @param height - Legend height in pixels
 * @returns ImageData for the legend
 */
export function generateLegendImage(
  colorScheme: HeatmapColorScheme,
  width = 200,
  height = 20,
): ImageData {
  const imageData = new ImageData(width, height);
  const data = imageData.data;
  const gradient = COLOR_GRADIENTS[colorScheme];

  for (let x = 0; x < width; x++) {
    const t = x / (width - 1);
    const [r, g, b, a] = interpolateColor(gradient, t);

    for (let y = 0; y < height; y++) {
      const idx = (y * width + x) * 4;
      data[idx] = r;
      data[idx + 1] = g;
      data[idx + 2] = b;
      data[idx + 3] = a;
    }
  }

  return imageData;
}

/**
 * Export heatmap as a PNG data URL
 *
 * @param imageData - Heatmap ImageData
 * @returns PNG data URL
 */
export function heatmapToDataURL(imageData: ImageData): string {
  const canvas = document.createElement("canvas");
  canvas.width = imageData.width;
  canvas.height = imageData.height;
  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get canvas context");
  ctx.putImageData(imageData, 0, 0);
  return canvas.toDataURL("image/png");
}

/**
 * Composite heatmap over a background image
 *
 * @param backgroundImage - Background image element
 * @param heatmapImageData - Heatmap ImageData
 * @param opacity - Heatmap opacity (0-1)
 * @returns Composited canvas
 */
export function compositeHeatmapOverBackground(
  backgroundImage: HTMLImageElement,
  heatmapImageData: ImageData,
  opacity = 1.0,
): HTMLCanvasElement {
  const canvas = document.createElement("canvas");
  canvas.width = heatmapImageData.width;
  canvas.height = heatmapImageData.height;

  const ctx = canvas.getContext("2d");
  if (!ctx) throw new Error("Could not get canvas context");

  // Draw background image, scaled to canvas size
  ctx.drawImage(backgroundImage, 0, 0, canvas.width, canvas.height);

  // Create temporary canvas for heatmap
  const heatmapCanvas = document.createElement("canvas");
  heatmapCanvas.width = heatmapImageData.width;
  heatmapCanvas.height = heatmapImageData.height;
  const heatmapCtx = heatmapCanvas.getContext("2d");
  if (!heatmapCtx) throw new Error("Could not get heatmap canvas context");
  heatmapCtx.putImageData(heatmapImageData, 0, 0);

  // Composite heatmap with opacity
  ctx.globalAlpha = opacity;
  ctx.drawImage(heatmapCanvas, 0, 0);
  ctx.globalAlpha = 1;

  return canvas;
}

/** Cached device capability detection result */
let cachedIsLowEndDevice: boolean | null = null;

/**
 * Detect if the current device has low performance capabilities
 * Uses hardware concurrency, device memory, and user agent heuristics
 *
 * @returns true if device is detected as low-end
 */
export function isLowEndDevice(): boolean {
  // Return cached result if available
  if (cachedIsLowEndDevice !== null) {
    return cachedIsLowEndDevice;
  }

  // Check hardware concurrency (number of logical CPU cores)
  const lowCores = navigator.hardwareConcurrency <= 2;

  // Check device memory (in GB) - Chrome/Edge only
  const deviceMemory = (navigator as { deviceMemory?: number }).deviceMemory;
  const lowMemory = deviceMemory !== undefined && deviceMemory <= 2;

  // Check user agent for mobile devices
  const isMobile = /Mobile|Android|iPhone|iPad|iPod/.test(navigator.userAgent);

  // Consider device low-end if any significant indicator is present
  cachedIsLowEndDevice = lowCores || lowMemory || isMobile;

  return cachedIsLowEndDevice;
}

/**
 * Get adaptive heatmap configuration based on device capabilities
 * Returns reduced resolution config for low-end devices
 *
 * @param baseConfig - Base configuration to merge with
 * @returns Configuration adapted for device capabilities
 */
export function getAdaptiveHeatmapConfig(
  baseConfig: Partial<HeatmapGenerationConfig> = {},
): HeatmapGenerationConfig {
  const defaultConfig = isLowEndDevice() ? LOW_END_HEATMAP_CONFIG : DEFAULT_HEATMAP_CONFIG;

  return {
    ...defaultConfig,
    ...baseConfig,
  };
}
